import { gql } from "@apollo/client";
import useStdQuery from "components/data/hooks/useStdQuery.js";
import { useTransitAccountIdContext } from "context/TransitAccountIdContext";
import { TRANSIT_ACCOUNT_FIELDS } from "../TransitAccountFragment";


export const RIDER_CLASSES = {
	fullFare: 1,
};


// Fetch TA from transitaccount/<transitaccount-id>/subsystem/<subsystem-id> GET
export const GET_TRANSIT_ACCOUNT = gql`
	${TRANSIT_ACCOUNT_FIELDS}
	query TransitAccountGET (
		$subsystemAccountReference: ID
		$returnArchivedTokens: Boolean!
		$returnSubscriptions: Boolean!
		$returnProgramMemberships: Boolean!
		$returnTransferProductInfo: Boolean!
		$returnProductPayments: Boolean!
		$returnOrderInfo: Boolean!
		$returnTapVoidInfo: Boolean!
		$returnTapCorrectionInfo: Boolean!
		$returnTravelTokenActions: Boolean!
	){
		transitAccountQ (
			subsystemAccountReference: $subsystemAccountReference
			returnProductPayments: $returnProductPayments
			returnOrderInfo: $returnOrderInfo
			returnTapVoidInfo: $returnTapVoidInfo
			returnTapCorrectionInfo: $returnTapCorrectionInfo
			returnTransferProductInfo: $returnTransferProductInfo
			returnSubscriptions: $returnSubscriptions
			returnProgramMemberships: $returnProgramMemberships
			returnArchivedTokens: $returnArchivedTokens
			returnTravelTokenActions: $returnTravelTokenActions
		){
			...TransitAccountFields
		}
	}
`;

// Fetch TA from transitaccount/<transitaccount-id>/subsystem/<subsystem-id> GET
export const GET_ALL_TRANSIT_ACCOUNTS = gql`
	${TRANSIT_ACCOUNT_FIELDS}
	query AllTransitAccountsGET (
		$returnArchivedTokens: Boolean!
		$returnSubscriptions: Boolean!
		$returnProgramMemberships: Boolean!
		$returnTransferProductInfo: Boolean!
		$returnProductPayments: Boolean!
		$returnOrderInfo: Boolean!
		$returnTapVoidInfo: Boolean!
		$returnTapCorrectionInfo: Boolean!
		$returnTravelTokenActions: Boolean!
	){
		allTransitAccountsQ (
			returnProductPayments: $returnProductPayments
			returnOrderInfo: $returnOrderInfo
			returnTapVoidInfo: $returnTapVoidInfo
			returnTapCorrectionInfo: $returnTapCorrectionInfo
			returnTransferProductInfo: $returnTransferProductInfo
			returnSubscriptions: $returnSubscriptions
			returnProgramMemberships: $returnProgramMemberships
			returnArchivedTokens: $returnArchivedTokens
			returnTravelTokenActions: $returnTravelTokenActions
		){
			nodeTime
			transitAccounts {
				...TransitAccountFields
			}
		}
	}
`;


export const getTransitAccountDefaultRefetchVariables = (subsystemAccountReference) =>  ({
	subsystemAccountReference,
	returnArchivedTokens: false,
	returnSubscriptions: false,
	returnProgramMemberships: false,
	returnTransferProductInfo: false,
	returnProductPayments: false,
	returnOrderInfo: false,
	returnTapVoidInfo: false,
	returnTapCorrectionInfo: false,
	returnTravelTokenActions: false,
});

export const getAllTransitAccountDefaultRefetchVariables = () =>  ({
	returnArchivedTokens: false,
	returnSubscriptions: false,
	returnProgramMemberships: false,
	returnTransferProductInfo: false,
	returnProductPayments: false,
	returnOrderInfo: false,
	returnTapVoidInfo: false,
	returnTapCorrectionInfo: false,
	returnTravelTokenActions: false,
});

export const getAllTransitAccountRefetchQueries = () => [
	{
		query: GET_ALL_TRANSIT_ACCOUNTS,
		variables: { ...getAllTransitAccountDefaultRefetchVariables() },
	},
];

export const getTransitAccountRefetchQueries = (subsystemAccountReference) => [
	{
		query: GET_TRANSIT_ACCOUNT,
		variables: {
			...getTransitAccountDefaultRefetchVariables(subsystemAccountReference),
			returnSubscriptions: true,
			returnProgramMemberships: true,
		},
	},
	{
		query: GET_TRANSIT_ACCOUNT,
		 variables: {
			...getTransitAccountDefaultRefetchVariables(subsystemAccountReference),
			returnSubscriptions: true,
		},
	},
	{ query: GET_TRANSIT_ACCOUNT, variables: getTransitAccountDefaultRefetchVariables(subsystemAccountReference) },
];

/**
 * @param {?string} subsystemAccountReference
 * @param archivedTokens
 * @param subscriptions
 * @param programMemberships
 * @param returnTransferProductInfo
 * @param productPayments
 * @param restProps
 * @return {{data: any, response: {subscribeToMore<TSubscriptionData=any, TSubscriptionVariables=OperationVariables extends OperationVariables>(options: SubscribeToMoreOptions<any, TSubscriptionVariables, TSubscriptionData>): () => void, variables: OperationVariables | undefined, data: any, called: boolean, startPolling(pollInterval: number): void, networkStatus: NetworkStatus, parsedError: any|null|null, refetch(variables?: Partial<OperationVariables>): Promise<ApolloQueryResult<any>>, reobserve(newOptions?: Partial<WatchQueryOptions<OperationVariables, any>>, newNetworkStatus?: NetworkStatus): Promise<ApolloQueryResult<any>>, error?: ApolloError, loading: boolean, updateQuery<TVars=OperationVariables extends OperationVariables>(mapFn: (previousQueryResult: any, options: Pick<WatchQueryOptions<TVars, any>, "variables">) => any): void, fetchMore<TFetchData=any, TFetchVars=OperationVariables extends OperationVariables>(fetchMoreOptions: (FetchMoreQueryOptions<TFetchVars, TFetchData> & {updateQuery?: (previousQueryResult: any, options: {fetchMoreResult: TFetchData, variables: TFetchVars}) => any})): Promise<ApolloQueryResult<TFetchData>>, stopPolling(): void, observable: ObservableQuery<any, OperationVariables>, client: ApolloClient<any>, queryName: any, previousData?: any}, loading: boolean}}
 */
export const useTransitAccount = (options = {}) => {
	const {
		subsystemAccountReference = null,
		returnArchivedTokens = false,
		returnSubscriptions = false,
		returnProgramMemberships = false,
		returnTransferProductInfo = false,
		returnProductPayments = false,
		returnOrderInfo = false,
		returnTapVoidInfo = false,
		returnTapCorrectionInfo = false,
		returnTravelTokenActions = false,
		...restProps
	} = options;
	// If no TA is specified try and get whatever TA we might already be in
	const transit_account_id = useTransitAccountIdContext();

	const response = useStdQuery(GET_TRANSIT_ACCOUNT, {
		...restProps,
		variables: {
			subsystemAccountReference: subsystemAccountReference ? subsystemAccountReference : transit_account_id,
			returnOrderInfo,
			returnArchivedTokens,
			returnSubscriptions,
			returnProgramMemberships,
			returnTransferProductInfo,
			returnProductPayments,
			returnTapVoidInfo,
			returnTapCorrectionInfo,
			returnTravelTokenActions,
		},
		skip: !subsystemAccountReference && !transit_account_id,
	});

	const { data, loading: loadingResponse, error } = response;

	return { data, loading: loadingResponse, error, response };
};

export const useAllTransitAccounts = ({
	returnArchivedTokens = false,
	returnSubscriptions = false,
	returnProgramMemberships = false,
	returnTransferProductInfo = false,
	returnProductPayments = false,
	returnOrderInfo = false,
	returnTapVoidInfo = false,
	returnTravelTokenActions = false,
	returnTapCorrectionInfo = false,
	...restProps
}) => {

	const response = useStdQuery(GET_ALL_TRANSIT_ACCOUNTS, {
		...restProps,
		variables: {
			returnOrderInfo,
			returnArchivedTokens,
			returnSubscriptions,
			returnProgramMemberships,
			returnTransferProductInfo,
			returnProductPayments,
			returnTapVoidInfo,
			returnTapCorrectionInfo,
			returnTravelTokenActions,
		},
	});

	const { data, loading: loadingResponse, error } = response;

	return {
		transitAccounts: data?.allTransitAccountsQ?.transitAccounts ?? [],
		data,
		loading: loadingResponse,
		error,
		response,
	};
};
